import type { ArchiveTier, ArrangementStatus } from '../schema/arrangement.schema';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  WHICH ACT PUT AN ARRANGEMENT IN THE ARCHIVE. [C1e §5]
 *
 *  ⚠️ IT EXISTS BECAUSE RESTORE HAS NO CLOCK TO ASK. `deleteImpact` decides a tier from
 *  four clock comparisons, which is right on the way IN — every term flips on its own, with
 *  no write to race. On the way BACK there is nothing to compare: the duties are archived
 *  and the question is not "what is true now" but "what was done to this record". So the
 *  record answers it, and this is the decoding.
 *
 *  ⚠️ IT IS A DECODING, NOT AN INFERENCE, AND THAT DISTINCTION IS THE WHOLE GUARANTEE.
 *  `archiveArrangement` is the ONLY writer in the repository of `Arrangement.status =
 *  'CANCELLED'`, and it writes it inside the archive transaction under exactly
 *  `impact.isIssued && !filing`. So `status === 'CANCELLED'` ⟺ "this archive was a
 *  cancellation", in both directions:
 *
 *    • a LIVE row can never hold it — there is no other writer;
 *    • a FILED order keeps its own status (`CLOSED`/`ACTIVE`), by that same `!filing`;
 *    • `restoreArrangement` rewrites `status` from `archived_from_status` before clearing
 *      the columns, so a restored row never holds it either.
 *
 *  `arrangementArchiveTiers.test.ts` pins that one-writer premise with a SOURCE SCAN, not
 *  with this paragraph — a second writer of `CANCELLED` would widen restore in silence.
 *
 *  ⚠️ AND THE AUDIT ROW IS NOT CONSULTED. `ARRANGEMENT_ARCHIVED`'s context does carry the
 *  tier, and reading it here would authorise a write from a log. An audit trail is
 *  evidence; it is not an access-control list, and `auditLog.model.ts`'s `context` is a
 *  `Map of String` with no schema to make that safe even if it were.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

/**
 * ⚠️ `BLOCKED` IS EXCLUDED IN THE RETURN TYPE, AND THAT IS THE GUARANTEE RATHER THAN A
 * CONVENIENCE. `purgePlan.util.ts`'s argument, second instance: the literal is in the type,
 * so widening it fails `tsc --noEmit` at the declaration before any test runs. BLOCKED
 * refuses the archive outright, so no archived record was ever put there by it — and
 * restore therefore has no parameter through which a caller could be refused on a tier
 * that means "nobody may act right now" rather than "not you".
 */
export type ArchivedTier = Exclude<ArchiveTier, 'BLOCKED'>;

/**
 * ⚠️ A TOTAL `switch` WITH NO `default`, so a seventh `ArrangementStatus` is a compile
 * error here rather than a silent `FILING`. `officerDutyStateOf` is the precedent for the
 * shape, and CLAUDE.md §10's ban is on a role `switch` — this is over a status.
 *
 * ⚠️ `DRAFT` GETS ITS OWN ANSWER RATHER THAN A SAFE ONE, AND THAT ANSWER IS LOAD-BEARING.
 * `deleteImpact`'s own branch table puts a draft in CANCELLATION ("no status change, no
 * event"), which is what this returns.
 *
 * ⚠️ IT IS NOT UNREACHABLE — this comment said so for one review cycle and was wrong.
 * `assertMayArchive` does short-circuit on a draft ORIGIN before asking for a tier, so the
 * SERVER never consults this arm; but `web/src/pages/ArrangementDetail` calls
 * `archivedTierOf(record.status)` to decide whether to offer Restore, and an archived draft
 * still holds `DRAFT`. A reader who trusts "unreachable" will not notice that the value gates
 * a control.
 */
export const archivedTierOf = (status: ArrangementStatus): ArchivedTier => {
  switch (status) {
    case 'CANCELLED':
      return 'CANCELLATION';
    case 'DRAFT':
      return 'CANCELLATION';
    case 'PUBLISHED':
    case 'ALLOCATION_IN_PROGRESS':
    case 'ACTIVE':
    case 'CLOSED':
      return 'FILING';
  }
};

/**
 * ⚠️ [C2b §4g] THE TIERS ARE ORDERED, AND A CORRIGENDUM FAMILY ANSWERS WITH ITS WORST MEMBER.
 *
 * §4g reads as three separate rules — cancel cascades, file only when the WHOLE group has
 * elapsed, a duty in progress on EITHER refuses BOTH — and they are one rule once the tiers
 * are ordered `BLOCKED > CANCELLATION > FILING`:
 *
 *   · one `BLOCKED` member blocks the family, which is the refusal;
 *   · one member still live makes the family `CANCELLATION` rather than `FILING`, which is
 *     "only when the whole group has elapsed" — filing is simply not the answer until it has;
 *   · and `CANCELLATION` reached that way is the cascade.
 *
 * ⚠️ A MAXIMUM RATHER THAN THREE `if`s, FOR [C1d §1]'s OWN REASON: the tier IS the policy, and
 * `assertMayArchive` is handed exactly one of them. Three branches would be three chances to
 * disagree with a function whose whole job is to decide from one value.
 *
 * ⚠️ AND THE RANK IS DECLARED, NOT THE TUPLE'S POSITION. `ARCHIVE_TIERS` is declaration order
 * and explicitly not a rank ([WINGS.1]'s `ORG_UNIT_SUBTYPES` lesson) — it happens to ascend
 * today, and the accident dies the first time somebody inserts a member.
 */
const ARCHIVE_TIER_SEVERITY: Record<ArchiveTier, number> = {
  FILING: 0,
  CANCELLATION: 1,
  BLOCKED: 2,
};

/** The tier a group of orders archives at. An empty list is `FILING` — nothing to withhold. */
export const worstArchiveTier = (tiers: readonly ArchiveTier[]): ArchiveTier =>
  tiers.reduce<ArchiveTier>(
    (worst, tier) => (ARCHIVE_TIER_SEVERITY[tier] > ARCHIVE_TIER_SEVERITY[worst] ? tier : worst),
    'FILING',
  );
